import { priorityFromMatrix, type MatrixPos, type MatrixScope } from "@app/core";
import { cn } from "@app/ui";
import { useState } from "react";
import type { I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { PriorityMatrix } from "../priority-matrix/PriorityMatrix.js";
import { PRIORITY_FLAG_HEX, matrixColorHex, matrixScoreOf } from "../task-colors.js";

const FLAG_LEVELS: Array<{ key: I18nKey; hex: string }> = [
  { key: "sidebar.priority.low", hex: PRIORITY_FLAG_HEX.low },
  { key: "sidebar.priority.medium", hex: PRIORITY_FLAG_HEX.medium },
  { key: "sidebar.priority.high", hex: PRIORITY_FLAG_HEX.high },
];

const SUMMARY_KEY: Record<MatrixScope, I18nKey> = {
  off: "demos.compare.summary.off",
  tasks: "demos.compare.summary.tasks",
  tasks_events: "demos.compare.summary.tasksEvents",
};

/**
 * STATIC flags-vs-matrix comparison for the matrix-scope settings card — the
 * "animation only when it adds information" counterpart to the ghost demos.
 * Renders the real widgets/color math inline (no iframe, no runner): the right
 * matrix is live local state, so the continuous diagonal color can be felt by
 * dragging the dot; nothing is persisted. `scope` mirrors the selected mode:
 * each panel carries "used for Tasks/Events" chips and the panel that governs
 * nothing is dimmed, so it's always visible which system applies to what.
 */
export function MatrixCompare({ scope }: { scope: MatrixScope }) {
  const { t } = useI18n();
  const [pos, setPos] = useState<MatrixPos | null>({ urgency: 0.78, importance: 0.72 });
  const accent = pos ? matrixColorHex(matrixScoreOf(pos)) : null;

  const flagsFor = { tasks: scope === "off", events: scope !== "tasks_events" };
  const matrixFor = { tasks: scope !== "off", events: scope === "tasks_events" };

  return (
    <div className="mt-4">
      <p className="text-sm text-muted-foreground">{t(SUMMARY_KEY[scope])}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <section
          className={cn(
            "rounded-lg border bg-muted/30 p-3 transition-opacity duration-200",
            !flagsFor.tasks && !flagsFor.events && "opacity-55",
          )}
        >
          <h4 className="text-sm font-medium">{t("demos.compare.flagsTitle")}</h4>
          <UsedFor tasks={flagsFor.tasks} events={flagsFor.events} />
          <div className="mt-3 flex flex-col gap-2">
            {FLAG_LEVELS.map(({ key, hex }) => (
              <div key={key} className="flex items-center gap-2">
                <span
                  className="h-4 flex-1 rounded"
                  style={{ background: `linear-gradient(0deg, ${hex}40, ${hex}40)` , boxShadow: `inset 3px 0 0 ${hex}` }}
                />
                <span className="w-16 text-xs text-muted-foreground">{t(key)}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{t("demos.compare.flagsNote")}</p>
        </section>

        <section
          className={cn(
            "rounded-lg border bg-muted/30 p-3 transition-opacity duration-200",
            !matrixFor.tasks && !matrixFor.events && "opacity-55",
          )}
        >
          <h4 className="text-sm font-medium">{t("demos.compare.matrixTitle")}</h4>
          <UsedFor tasks={matrixFor.tasks} events={matrixFor.events} />
          <div className="mt-3 flex flex-wrap items-start gap-3">
            <PriorityMatrix value={pos} priority={pos ? priorityFromMatrix(pos) : "none"} onChange={setPos} />
            <div className="flex min-w-24 flex-1 flex-col gap-2">
              <span
                className="h-4 rounded"
                style={
                  accent
                    ? { background: `linear-gradient(0deg, ${accent}40, ${accent}40)`, boxShadow: `inset 3px 0 0 ${accent}` }
                    : { background: "var(--muted)" }
                }
              />
              <p className="text-xs text-muted-foreground">{t("demos.compare.matrixNote")}</p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

/** "Used for: [✓ Tasks] [✓ Events]" chip row under a panel title; a dashed
 *  "not used" chip when the panel governs nothing in the selected mode. */
function UsedFor({ tasks, events }: { tasks: boolean; events: boolean }) {
  const { t } = useI18n();
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="text-[calc(11px*var(--app-text-scale))] text-muted-foreground">{t("demos.compare.usedFor")}</span>
      {!tasks && !events ? (
        <span className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-[calc(11px*var(--app-text-scale))] text-muted-foreground">
          {t("demos.compare.notUsed")}
        </span>
      ) : (
        <>
          {tasks && <EntityChip label={t("demos.compare.chip.tasks")} />}
          {events && <EntityChip label={t("demos.compare.chip.events")} />}
        </>
      )}
    </div>
  );
}

function EntityChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[calc(11px*var(--app-text-scale))] font-medium text-primary">
      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" aria-hidden>
        <path d="M2 6.5 5 9.5 10 3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label}
    </span>
  );
}
